/**
 * App state. Zustand, deliberately thin — the interesting logic lives in
 * `engine/` and `@catchup/shared`, both framework-free, so it can be tested
 * without a browser and ported without a rewrite.
 */

import { create } from "zustand";
import type {
  CalibrationResult,
  Chart,
  ScoreResult,
  ScoringConfig,
  SessionAttempt,
  Tier,
} from "@catchup/shared";
import { DEFAULT_CONFIG } from "@catchup/shared";
import type { AudioEngine } from "./engine/audio.ts";
import type { RoundCandidate } from "./engine/round.ts";
import type { LibraryEntry } from "./api.ts";

export type Screen = "library" | "add" | "calibrate" | "pick-round" | "play" | "results";

interface State {
  screen: Screen;
  config: ScoringConfig;
  mode: "local" | "hosted";
  storage: string;
  uploadsGated: boolean;
  uploadsConfigured: boolean;
  maxUploadBytes: number;

  /** Public catalogue of charted tracks. */
  entries: LibraryEntry[];
  libraryError: string | null;
  loadingLibrary: boolean;

  /** Whether the visitor has entered a working contribution passphrase. */
  authed: boolean;

  chart: Chart | null;
  buffer: AudioBuffer | null;
  loadingTrack: boolean;
  trackError: string | null;

  engine: AudioEngine | null;
  calibration: CalibrationResult | null;
  calibrationBlocked: string | null;

  tier: Tier;
  rounds: RoundCandidate[];
  round: RoundCandidate | null;

  result: ScoreResult | null;
  lastAttempt: SessionAttempt | null;

  set: (patch: Partial<State>) => void;
  backToLibrary: () => void;
}

export const useStore = create<State>((set) => ({
  screen: "library",
  config: DEFAULT_CONFIG,
  mode: "hosted",
  storage: "local",
  uploadsGated: true,
  uploadsConfigured: true,
  maxUploadBytes: 95_000_000,

  entries: [],
  libraryError: null,
  loadingLibrary: false,

  authed: false,

  chart: null,
  buffer: null,
  loadingTrack: false,
  trackError: null,

  engine: null,
  calibration: null,
  calibrationBlocked: null,

  tier: "assisted",
  rounds: [],
  round: null,

  result: null,
  lastAttempt: null,

  set: (patch) => set(patch),
  backToLibrary: () =>
    set({
      screen: "library",
      chart: null,
      buffer: null,
      rounds: [],
      round: null,
      result: null,
      trackError: null,
    }),
}));
